// tutorialsData.js
// Zentrale Datenverwaltung für alle Tutorials

// Hilfsfunktion für Bildpfade, die auch in Produktion funktioniert
const getImagePath = (path) => {
  // process.env.PUBLIC_URL wird zur Build-Zeit von Create React App eingefügt
  // Im Root-Deployment ist es leer '', bei Subpfad-Deployment enthält es den Pfad
  const publicUrl = process.env.PUBLIC_URL || ''
  
  // Stelle sicher, dass der Pfad mit / beginnt
  const cleanPath = path.startsWith('/') ? path : `/${path}`
  
  // Bestimme den finalen Pfad
  let finalPath
  if (publicUrl) {
    // Entferne trailing slash von publicUrl falls vorhanden
    const cleanPublicUrl = publicUrl.endsWith('/') ? publicUrl.slice(0, -1) : publicUrl
    finalPath = `${cleanPublicUrl}${cleanPath}`
  } else {
    // Im Root-Deployment verwenden wir einfach den absoluten Pfad
    finalPath = cleanPath
  }
  

  
  return finalPath
}

export const tutorialsData = [
    {
      id: 'wissen-hinzufuegen',
      title: 'Wissen hinzufügen',
      description: 'Lerne, wie du dein Wissen strukturiert in die Datenbank einpflegst, damit Kollegen weltweit davon profitieren können.',
      icon: null,
      category: 'Grundlagen',
      duration: '5 min',
      steps: [
        {
          id: 1,
          title: 'Den Vorgang starten',
          description: 'Wie du den Prozess zum Hinzufügen von Wissen beginnst',
          image: getImagePath('/uploads/WissenHinzufuegen.jpeg'),
          content: `
            <p>Klicke in der oberen Navigationsleiste auf <strong>"Wissen hinzufügen"</strong>.</p>
            
            <div class="info-box">
              <strong>💡 Hinweis zu Entwürfen:</strong> Falls du einen Beitrag beginnst, aber nicht sofort fertigstellst, 
              wird dieser automatisch unter "Meine Entwürfe" gespeichert. Du kannst ihn dort jederzeit wieder 
              aufrufen und weiterbearbeiten.
            </div>
          `
        },
        {
          id: 2,
          title: 'Schritt 1: Basis-Informationen',
          description: 'Grundlegende Daten zum Problem erfassen',
          image: getImagePath('/uploads/Step1.jpeg'),
          content: `
            <p>Fülle die grundlegenden Daten zum Problem aus:</p>
            
            <ul>
              <li>
                <strong>Gerät & Kategorie:</strong> Wähle das entsprechende Gerät und die passende 
                Kategorie (z. B. <em>Troubleshooting</em> oder <em>Fehlercode</em>) aus.
              </li>
              <li>
                <strong>Fehlercode (optional):</strong> Gib hier, falls vorhanden, den spezifischen 
                Code ein (z. B. 11214).
              </li>
              <li>
                <strong>Titel:</strong> Wähle einen aussagekräftigen Titel (z. B. "Low Energy Fehler").
              </li>
              <li>
                <strong>Beschreibung (optional):</strong> Beschreibe das Problem hier bei Bedarf genauer.
              </li>
            </ul>
            
            <p>Klicke unten rechts auf <strong>"Weiter"</strong>, um fortzufahren.</p>
          `
        },
        {
          id: 3,
          title: 'Schritt 2: Lösung und Medien',
          description: 'Den Lösungsweg dokumentieren',
          image: getImagePath('/uploads/Step2.jpeg'),
          content: `
            <p>Hier dokumentierst du den Lösungsweg:</p>
            
            <ul>
              <li>
                <strong>Lösungstext:</strong> Beschreibe die Schritte zur Behebung des Problems.
              </li>
              <li>
                <strong>Bilder hinzufügen:</strong> Ziehe Fotos einfach per <em>Drag-and-Drop</em> 
                in das markierte Feld oder nutze den Button "Durchsuchen".
              </li>
              <li>
                <strong>Bilder benennen & verknüpfen:</strong> Unter den hochgeladenen Fotos kannst 
                du den Namen per Klick ändern. Um im Lösungstext auf ein Bild zu verweisen, nutze 
                einfach das <code>@-Symbol</code> gefolgt vom Bildnamen (z. B. <code>@bild1</code>).
              </li>
              <li>
                <strong>Komponenten:</strong> Liste betroffene Bauteile (z. B. Blitzlampe, Pumpkammer) 
                kommagetrennt auf.
              </li>
            </ul>
          `
        },
        {
          id: 4,
          title: 'Schritt 3: Zusatzdetails',
          description: 'Optional: Hilfreiche Zusatzinformationen',
          image: getImagePath('/uploads/Step3.jpeg'),
          content: `
            <p>Diese Angaben sind freiwillig, helfen aber bei der Planung der Reparatur:</p>
            
            <ul>
              <li>
                <strong>Schwierigkeitsgrad:</strong> Wie komplex ist die Lösung?
              </li>
              <li>
                <strong>Geschätzte Dauer:</strong> Wie lange dauert die Behebung etwa?
              </li>
              <li>
                <strong>Benötigtes Werkzeug:</strong> Liste die nötigen Tools kommagetrennt auf.
              </li>
            </ul>
          `
        },
        {
          id: 5,
          title: 'Schritt 4: Überprüfung & Einreichen',
          description: 'Finaler Check und Absenden',
          image: null,
          content: `
            <p>Du siehst nun eine <strong>Vorschau</strong> deines gesamten Beitrags. Kontrolliere alle 
            Angaben noch einmal auf Richtigkeit.</p>
            
            <p>Wenn alles passt, klicke auf den Button unten rechts, um das Wissen final einzureichen.</p>
            
            <div class="info-box success">
              <strong>✅ Was passiert nach dem Einreichen?</strong>
              <p>Dein Beitrag wird nicht sofort veröffentlicht. Er geht zunächst an einen 
              Moderator/Administrator (Gerätespezialisten oder 2nd Level Support). Diese prüfen 
              den Inhalt, ergänzen gegebenenfalls Informationen und geben den Beitrag anschließend 
              für alle Nutzer der Datenbank frei.</p>
            </div>
          `
        }
      ]
    },
    {
      id: 'wissen-moderieren-und-verwalten',
      title: 'Wissen moderieren & verwalten',
      description: 'Lerne, wie du eingereichte Beiträge moderierst, freigibst, erneut bearbeiten lässt oder löschst.',
      icon: null,
      category: 'Moderation',
      duration: '4 min',
      steps: [
        {
          id: 1,
          title: 'Zur Moderation wechseln',
          description: 'Offene Einreichungen finden und zur Moderationsseite wechseln.',
          image: getImagePath('/uploads/tutorial-moderation-nav.png'),
          content: `
            <p>Öffne die Seite <strong>„Moderieren“</strong>. Die rote Zahl am Menüpunkt zeigt die Anzahl offener Beiträge.</p>
          `
        },
        {
          id: 2,
          title: 'Filtern und sortieren',
          description: 'Beiträge nach Geräten oder Kategorien eingrenzen.',
          image: getImagePath('/uploads/tutorial-moderation-filter.png'),
          content: `
            <p>Nutze die Filterleiste oben, um nach <strong>Geräten</strong> oder <strong>Kategorien</strong> zu filtern.</p>
          `
        },
        {
          id: 3,
          title: 'Im Moderationsmenü entscheiden',
          description: 'Beitrag bearbeiten, ablehnen oder freigeben.',
          image: getImagePath('/uploads/tutorial-moderation-menue.png'),
          content: `
            <ul>
              <li><strong>Bearbeiten:</strong> Oben rechts auf <em>Bearbeiten</em> klicken, um den Beitrag zu verbessern.</li>
              <li><strong>Ablehnen:</strong> Unten links auf <em>Ablehnen</em>, um den Beitrag zu löschen.</li>
              <li><strong>Wissen freigeben:</strong> Unten rechts freigeben, damit alle Nutzer Zugriff erhalten.</li>
            </ul>
          `
        },
        {
          id: 4,
          title: 'Freigegebenes Wissen erneut bearbeiten oder löschen',
          description: 'Bestehende Beiträge zurück in die Moderation holen oder endgültig entfernen.',
          image: getImagePath('/uploads/tutorial-moderation-reedit.png'),
          content: `
            <p>Scrolle zum Ende des Beitrags:</p>
            <ul>
              <li><strong>Re-Edit:</strong> Verschiebt den Beitrag zurück zur Moderationsseite, um ihn erneut zu bearbeiten.</li>
              <li><strong>Wissen löschen:</strong> Entfernt den Beitrag dauerhaft (nicht reversibel).</li>
            </ul>
          `
        },
        {
          id: 5,
          title: 'Versionshistorie prüfen',
          description: 'Änderungsverlauf nachvollziehen.',
          image: getImagePath('/uploads/tutorial-moderation-history.png'),
          content: `
            <p>Unterhalb der Buttons findest du die <strong>Versionshistorie</strong> mit allen bisherigen Versionen.</p>
          `
        }
      ]
    }
    // Hier können weitere Tutorials hinzugefügt werden:
    // {
    //   id: 'wissen-suchen',
    //   title: 'Wissen durchsuchen',
    //   description: 'Erfahre, wie du effektiv nach Lösungen suchst',
    //   icon: '🔍',
    //   category: 'Grundlagen',
    //   duration: '3 min',
    //   steps: [...]
    // }
  ]